import { isWorkflowRunTerminal } from "@digital-twins/shared-types";

import type { ThreadArtifactKind, WorkflowRun } from "@digital-twins/shared-types";

/**
 * A12: the side pane's one list, in the owner's order. Each section renders
 * only when it has something in it. `calculations` is O1's default home for
 * the newest answer's figures (not user files), shown last. `sources` lists
 * every source the thread cited, once each, the most cited first.
 */
export const SESSION_SECTION_ORDER = [
  "progress",
  "reports",
  "quick-studies",
  "studies",
  "chats",
  "artifacts",
  "files",
  "sources",
  "calculations",
] as const;

export type SessionSectionId = (typeof SESSION_SECTION_ORDER)[number];

/** i18n key under `threads.sessions.sections` for each section label. */
export const SESSION_SECTION_LABEL_KEYS = {
  progress: "threads.sessions.sections.progress",
  reports: "threads.sessions.sections.reports",
  "quick-studies": "threads.sessions.sections.quickStudies",
  studies: "threads.sessions.sections.studies",
  chats: "threads.sessions.sections.chats",
  artifacts: "threads.sessions.sections.artifacts",
  files: "threads.sessions.sections.files",
  sources: "threads.sessions.sections.sources",
  calculations: "threads.sessions.sections.calculations",
} as const satisfies Record<SessionSectionId, string>;

const REPORT_KINDS: ReadonlySet<ThreadArtifactKind> = new Set<ThreadArtifactKind>([
  "report",
  "findings",
  "study_doc",
  "digest",
]);

/**
 * Where a living artifact lists, by its closed kind only (no template, run or
 * slug heuristics). Written outputs a reader reads go to Reports; inputs and
 * working material (briefs, creatives, question sets, image stimuli) go to
 * Artifacts. A legacy item with no kind is an artifact.
 */
export function sessionSectionForArtifact(
  kind: ThreadArtifactKind | undefined,
): "reports" | "artifacts" {
  return kind && REPORT_KINDS.has(kind) ? "reports" : "artifacts";
}

/**
 * A run lists in Progress while it runs or while a step failed; a finished
 * run lists in Reports. Applies to the thread's latest run and to past runs
 * alike, so a completed latest run never stays in Progress.
 */
export function isProgressRun(run: WorkflowRun): boolean {
  if (!isWorkflowRunTerminal(run.status) || run.status === "failed") {
    return true;
  }
  return (run.steps ?? []).some((step) => step.state === "failed");
}
